import { default as SanityBlockContent } from "@sanity/block-content-to-react";
import classnames from "classnames";
import React, { ReactNode, useContext } from "react";
import FeaturedMakes from "../../components/FeaturedMakes/FeaturedMakes";
import SetOfMakes from "../../components/SetOfMakes/SetOfMakes";
import { SanityImage, SanityRawContent } from "../../model/common";
import { ComponentWidth } from "../../model/landing-page";
import BlockQuote from "../BlockQuote/BlockQuote";
import Callout, { CalloutProps } from "../Callout/Callout";
import CallToActionBar from "../CallToActionBar/CallToActionBar";
import CallToActionButton from "../CallToActionButton/CallToActionButton";
import CardHero from "../CardHero/CardHero";
import CardList from "../CardList/CardList";
import CollapsibleSections from "../CollapsibleSections/CollapsibleSections";
import Divider from "../Divider/Divider";
import EditorButtonBar from "../EditorButtonBar/EditorButtonBar";
import ExternalLink from "../ExternalLink/ExternalLink";
import FeaturedStatisticsList from "../FeaturedStatisticsList/FeaturedStatisticsList";
import FoundationValueGrid from "../FoundationValueGrid/FoundationValueGrid";
import GenericEmbed from "../GenericEmbed/GenericEmbed";
import ImageBlock, { ImageBlockImage } from "../ImageBlock/ImageBlock";
import InternalLink from "../InternalLink/InternalLink";
import MediaPair, { MediaGrid } from "../MediaPair/MediaPair";
import MissionStatementGrid from "../MissionStatementGrid/MissionStatementGrid";
import OrganisationGrid from "../OrganisationGrid/OrganisationGrid";
import PersonGrid from "../PersonGrid/PersonGrid";
import { Ratio } from "../Ratio/Ratio";
import SimpleTable from "../SimpleTable/SimpleTable";
import WithSpacing from "../Spacing/Spacing";
import StepperBar from "../Stepper/StepperBar/StepperBar";
import StepperCards from "../Stepper/StepperCards/StepperCards";
import StepperMenu from "../Stepper/StepperMenu/StepperMenu";
import Tabs, { TabsProps } from "../Tabs/Tabs";
import Text, { TextVariant } from "../Text/Text";
import YoutubeVideo from "../YoutubeVideoBlock/YoutubeVideoBlock";
import styles from "./BlockContent.module.scss";
import BlockContentBlock from "./BlockContentBlock";

export type CustomEmbeds = any;

export type ImageComponent = (props: {
  className?: string;
  loading: "auto" | "eager" | "lazy";
  node: SanityImage;
  quality: number;
  width: number | undefined;
}) => JSX.Element | null;

export interface BlockContentImageOptions {
  Image: ImageComponent | null;
}

const defaultImageOptions: BlockContentImageOptions = {
  Image: ImageBlockImage
};

const BlockContentImageOptionsContext = React.createContext(
  defaultImageOptions
);

export const CustomBlockContentImageOptions = ({
  options,
  children
}: {
  options: BlockContentImageOptions;
  children: ReactNode;
}) => (
  <BlockContentImageOptionsContext.Provider value={options}>
    {children}
  </BlockContentImageOptionsContext.Provider>
);

export const useBlockContentImageOptions = (): BlockContentImageOptions => {
  return useContext(BlockContentImageOptionsContext);
};

export type Cap = "3/4" | "2/3" | "none";

export interface BlockContentProps {
  /**
   * Note that, unlike Sanity, we default this to true.
   */
  renderContainerOnSingleChild?: boolean;
  className?: string;
  bodyVariant?: TextVariant;
  /**
   * Highlight the first paragraph.
   * Default false.
   */
  highlightFirstParagraph?: boolean;
  /**
   * Added inside the spaced content area before the main content.
   */
  before?: ReactNode;
  content: SanityRawContent;
  /**
   * Added inside the spaced content area after the main content.
   */
  after?: ReactNode;
  customEmbeds?: CustomEmbeds;
  location?: Location;
  /**
   * Defaults to lazy.
   */
  imageLoading?: "lazy" | "eager" | "auto";
  cap?: Cap;
}

// This should use srcSet etc. in future but for now we just
// default to something that fits the content cards.
const halfWidthImage = 435;

const BlockContent = ({
  bodyVariant = "defaultLight",
  className,
  before,
  content,
  after,
  highlightFirstParagraph,
  renderContainerOnSingleChild = true,
  customEmbeds = {},
  imageLoading = "lazy",
  cap = "none"
}: BlockContentProps) => {
  const imageMaxWidth = cap === "none" ? 870 : 653;
  const widthCapStyle = styles["cap" + cap.replace("/", "")];

  const serializers = {
    marks: {
      internalLink: ({ mark, children }: any) => (
        <InternalLink {...mark}>{children}</InternalLink>
      ),
      link: ExternalLink,
      // These are pulled up to mark their containing block with an ID.
      namedAnchor: ({ children }: any) => children
    },
    listItem: ({ children }: any) => (
      <Text variant={bodyVariant} as="li">
        {children}
      </Text>
    ),
    list: ({ children, type }: any) => {
      const isBulletColumns = type === "bullet-columns";
      return (
        <Text
          variant={bodyVariant}
          as={type === "bullet" || isBulletColumns ? "ul" : "ol"}
          className={classnames({ [styles.bulletColumns]: isBulletColumns })}
        >
          {children}
        </Text>
      );
    },
    types: {
      // Before/after fake blocks to get before/after as siblings so it spaces naturally.
      [BEFORE_BLOCK._type]: () => before,
      [AFTER_BLOCK._type]: () => after,

      block: (props: any) => (
        <BlockContentBlock bodyVariant={bodyVariant} {...props} />
      ),
      blockQuote: BlockQuote,
      callout: ({ node }: CalloutProps) => (
        <Callout className={styles.callout} node={node} />
      ),
      callToActionBar: CallToActionBar,
      callToActionButton: CallToActionButton,
      cardList: CardList,
      collapsibleSections: CollapsibleSections,
      contentHero: CardHero,
      divider: ({ node }: { node: { componentWidth: ComponentWidth } }) => (
        <Divider
          className={classnames(`${node.componentWidth}Width`, styles.divider)}
          useCustomMargin={false}
        />
      ),
      editorButtonBar: EditorButtonBar,
      featuredMakes: FeaturedMakes,
      featuredStatisticsList: FeaturedStatisticsList,
      foundationValueGrid: FoundationValueGrid,
      iframe: GenericEmbed,
      imageGrid: MediaGrid,
      imagePair: MediaPair,
      missionStatementGrid: MissionStatementGrid,
      organisationGrid: OrganisationGrid,
      personGrid: PersonGrid,
      ratio: Ratio,
      setOfMakes: SetOfMakes,
      simpleImage: ({ node }: { node: SanityImage }) => (
        <ImageBlock
          loading={imageLoading}
          width={node.halfWidth ? halfWidthImage : imageMaxWidth}
          node={node}
        />
      ),
      simpleTable: SimpleTable,
      // Legacy, but Make it: code it is using these. We should script a migration.
      standardImage: ({ node }: { node: SanityImage }) => (
        <ImageBlock
          loading={imageLoading}
          width={node.halfWidth ? halfWidthImage : imageMaxWidth}
          node={node}
        />
      ),
      stepperBar: StepperBar,
      stepperCards: StepperCards,
      stepperMenu: StepperMenu,
      tabs: ({ node }: TabsProps) => (
        <Tabs node={node} customEmbeds={customEmbeds} cap={cap} />
      ),
      videoPair: MediaPair,
      youtubeVideo: YoutubeVideo,
      // Often you can just add above, but sometimes you need context from the page.
      // Custom embeds  an override built-ins to render with more context, for example
      // with microbitCode. Perhaps we should refactor to use React context.
      ...customEmbeds
      // Placeholders for WIP, e.g. foo: () => null
    }
  };
  if (!content) {
    return null;
  }
  const contentWithBeforeAfter = [
    ...(before ? [BEFORE_BLOCK] : []),
    ...content,
    ...(after ? [AFTER_BLOCK] : [])
  ];
  return (
    <WithSpacing>
      <SanityBlockContent
        renderContainerOnSingleChild={renderContainerOnSingleChild}
        className={classnames(
          className,
          styles.root,
          widthCapStyle && styles.capped,
          widthCapStyle,
          {
            [styles.highlightFirstParagraph]: highlightFirstParagraph
          }
        )}
        projectId={process.env.GATSBY_SANITY_PROJECT_ID}
        dataset={process.env.GATSBY_SANITY_DATASET}
        serializers={serializers}
        blocks={contentWithBeforeAfter}
      />
    </WithSpacing>
  );
};

const BEFORE_BLOCK = { _type: "__before", children: [] };
const AFTER_BLOCK = { _type: "__after", children: [] };

export default BlockContent;
